import { FormSelect, Tooltip } from '@systemeio/ui'
import { AffiliateDashboardLeadOptionSource } from 'modules/affiliate-dashboard/components/affiliate-dashboard-lead-option-source'
import { useAffiliateDashboardLeads } from 'modules/affiliate-dashboard/hooks/use-affiliate-dashboard-leads'
import {
  AffiliateDashboardFilterInterface,
  AffiliateDashboardFilterWithTagInterface,
  TagId,
} from 'modules/affiliate-dashboard/types/affiliate-dashboard-interface'
import {
  AffiliateDashboardLeadOptionInterface,
  AffiliateDashboardLeadsInterface,
} from 'modules/affiliate-dashboard/types/affiliate-dashboard-leads-interface'
import React, { useEffect, useState } from 'react'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { SelectorDataInterface } from 'shared/types/selector-data-interface'
import { dateFormat, dateTimeFormat } from 'shared/utils/date-time-format'
import { ArrowDownIcon } from '@systemeio/ui'

interface AffiliateDashboardLeadsProps {
  mainFilter: AffiliateDashboardFilterInterface
  defaultFilter: AffiliateDashboardFilterInterface
  tagsData: SelectorDataInterface<TagId>[] | undefined
  setMainFilter: React.Dispatch<React.SetStateAction<AffiliateDashboardFilterInterface>>
}

const AffiliateDashboardLeads = ({
  mainFilter,
  tagsData,
  defaultFilter,
  setMainFilter,
}: AffiliateDashboardLeadsProps) => {
  const { t } = useLocoTranslation()
  const { limit, setLimit, pagination, setPagination, clearPagination } = usePagination(true)

  const [opened, setOpened] = useState<Record<number, boolean>>({})

  const [filterWithTag, setFilterWithTag] = useState<AffiliateDashboardFilterWithTagInterface>({
    ...defaultFilter,
    affiliateTag: undefined,
  })

  const changeTag = (tagId: TagId) => {
    setFilterWithTag(prev => ({ ...prev, affiliateTag: tagId }))
    clearPagination()
  }

  useEffect(() => {
    setFilterWithTag(prev => ({ ...prev, ...mainFilter }))
  }, [mainFilter])

  useEffect(() => {
    clearPagination()
  }, [mainFilter, clearPagination])

  const { affiliateLeads, counter } = useAffiliateDashboardLeads({
    filter: filterWithTag,
    pagination,
    limit,
  })

  useEffect(() => {
    if (affiliateLeads) {
      setOpened(
        affiliateLeads.items.reduce((acc, cur) => {
          return { ...acc, [cur.contact.id]: false }
        }, {}),
      )
    }
  }, [affiliateLeads])

  const columns: ColumnDefinitionInterface<AffiliateDashboardLeadsInterface>[] = [
    {
      key: 'email',
      header: t('dashboard.affiliate_dashboard.vendor'),
      widthPercent: 30,
      onRender: lead => (
        <div className={'flex items-center gap-1.5 truncate'}>
          <button
            onClick={() => setMainFilter(prev => ({ ...prev, vendorId: lead.contact.user.id }))}
            className={
              'ring-brand-primary h-[32px] w-[32px] shrink-0 rounded-full outline-hidden ring-offset-2 focus-visible:ring-2'
            }
          >
            <img
              alt={lead.contact.user.affiliateProgramBrandName}
              className={'h-[32px] w-[32px] rounded-full'}
              src={lead.contact.user.avatar}
            />
          </button>
          <div className={'flex w-full flex-col truncate'}>
            <span className={'truncate'}>{lead.contact.user.affiliateProgramBrandName}</span>
            <span className={'block truncate'}>{lead.contactEmail}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'affiliateOptins',
      header: t('dashboard.affiliate_dashboard.source_url'),
      widthPercent: 50,
      onRender: lead => (
        <AffiliateReferOptions opened={opened[lead.contact.id]} changeTag={changeTag} lead={lead} />
      ),
      onRenderSmall: lead => (
        <AffiliateMobileOptions
          opened={opened[lead.contact.id]}
          changeTag={changeTag}
          lead={lead}
          setOpened={setOpened}
        />
      ),
    },
    {
      key: 'affiliateOptins',
      header: t('global.date'),
      widthPercent: 20,
      onRender: lead => (
        <AffiliateDateOptions lead={lead} opened={opened[lead.contact.id]} setOpened={setOpened} />
      ),
      onRenderSmall: () => <></>,
    },
  ]
  return (
    <ListingLayout
      counter={counter}
      data={affiliateLeads}
      title={t('dashboard.affiliate.leads.title')}
      type={'compact'}
      customHeaderElement={
        <FormSelect
          placeholder={t('dashboard.affiliate_dashboard.any_tag')}
          value={filterWithTag.affiliateTag}
          data={tagsData}
          onChange={data => setFilterWithTag(prev => ({ ...prev, affiliateTag: data }))}
        />
      }
    >
      <Table
        nestedId={'contact'}
        data={affiliateLeads}
        columns={columns}
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
      />
    </ListingLayout>
  )
}

const AffiliateMobileOptions = ({
  lead,
  changeTag,
  opened,
  setOpened,
}: {
  lead: AffiliateDashboardLeadsInterface
  changeTag: (tagId: TagId) => void
  opened: boolean
  setOpened: React.Dispatch<React.SetStateAction<Record<number, boolean>>>
}) => {
  return (
    <div className={'flex w-full items-center justify-between gap-5 truncate'}>
      <ul className={'flex w-full flex-col gap-3 truncate'}>
        {lead.affiliateOptins.map((opt, idx) => {
          if (idx === 0) {
            return <AffiliateMobileOptionItem key={idx} item={opt} changeTag={changeTag} />
          } else if (opened) {
            return <AffiliateMobileOptionItem key={idx} item={opt} changeTag={changeTag} />
          }
        })}
      </ul>
      {lead.affiliateOptins.length > 1 && (
        <button
          onClick={() =>
            setOpened(prev => ({ ...prev, [lead.contact.id]: !prev[lead.contact.id] }))
          }
          className={
            'bg-brand-primary ring-brand-primary flex h-5 w-5 shrink-0 items-center justify-center rounded-full outline-hidden ring-offset-2 focus-visible:ring-2'
          }
        >
          <ArrowDownIcon
            className={`stroke-background-secondary h-3 w-3 ${
              opened && 'rotate-180'
            } transition-transform duration-200`}
          />
        </button>
      )}
    </div>
  )
}

const AffiliateMobileOptionItem = ({
  item,
  changeTag,
}: {
  item: AffiliateDashboardLeadOptionInterface
  changeTag: (tagId: TagId) => void
}) => {
  const { user } = useUser()

  return (
    <li className={'flex flex-col'}>
      <div className={'flex items-center gap-3'}>
        <AffiliateDashboardLeadOptionSource
          sourceUrl={item.sourceUrl}
          sourceText={item.sourceText}
        />
        {item.affiliateTag && (
          <button
            onClick={() => item.affiliateTag && changeTag(item.affiliateTag.id)}
            className={`ring-brand-primary bg-background-primary text-text-primary h-fit w-fit rounded-lg px-2 py-1 outline-hidden ring-offset-2 focus-visible:ring-2`}
          >
            {item.affiliateTag.name}
          </button>
        )}
      </div>
      <span className={`w-full truncate`}>
        {dateTimeFormat(item.date, {
          dateFormat: user?.dateFormat,
          timeFormat: user?.timeFormat,
          timeZone: user?.timezone,
        })}
      </span>
    </li>
  )
}

const AffiliateReferOptions = ({
  lead,
  changeTag,
  opened,
}: {
  lead: AffiliateDashboardLeadsInterface
  changeTag: (tagId: TagId) => void
  opened: boolean
}) => {
  return (
    <ul className={'flex w-full flex-col gap-1.5 truncate'}>
      {lead.affiliateOptins.map((opt, idx) => {
        if (idx === 0) {
          return <AffiliateReferOptionItem key={idx} item={opt} changeTag={changeTag} />
        } else if (opened) {
          return <AffiliateReferOptionItem key={idx} item={opt} changeTag={changeTag} />
        }
      })}
    </ul>
  )
}

const AffiliateDateOptions = ({
  lead,
  opened,
  setOpened,
}: {
  lead: AffiliateDashboardLeadsInterface
  opened: boolean
  setOpened: React.Dispatch<React.SetStateAction<Record<number, boolean>>>
}) => {
  return (
    <div className={'flex items-center justify-between gap-5 truncate'}>
      <ul className={'flex w-full flex-col gap-1.5 truncate'}>
        {lead.affiliateOptins.map((opt, idx) => {
          if (idx === 0) {
            return <AffiliateDateOptionItem key={idx} item={opt} />
          } else if (opened) {
            return <AffiliateDateOptionItem key={idx} item={opt} />
          }
        })}
      </ul>
      {lead.affiliateOptins.length > 1 && (
        <button
          onClick={() =>
            setOpened(prev => ({ ...prev, [lead.contact.id]: !prev[lead.contact.id] }))
          }
          className={
            'bg-brand-primary ring-brand-primary flex h-5 w-5 shrink-0 items-center justify-center rounded-full outline-hidden ring-offset-2 focus-visible:ring-2'
          }
        >
          <ArrowDownIcon
            className={`stroke-background-secondary h-3 w-3 ${
              opened && 'rotate-180'
            } transition-transform duration-200`}
          />
        </button>
      )}
    </div>
  )
}

const AffiliateReferOptionItem = ({
  item,
  changeTag,
}: {
  item: AffiliateDashboardLeadOptionInterface
  changeTag: (tagId: TagId) => void
}) => (
  <li className={'flex items-center justify-between gap-3'}>
    <AffiliateDashboardLeadOptionSource sourceUrl={item.sourceUrl} sourceText={item.sourceText} />
    {item.affiliateTag && (
      <button
        onClick={() => item.affiliateTag && changeTag(item.affiliateTag.id)}
        className={`ring-brand-primary bg-background-primary text-text-primary h-fit w-fit rounded-lg px-2 py-1 outline-hidden ring-offset-2 focus-visible:ring-2`}
      >
        {item.affiliateTag.name}
      </button>
    )}
  </li>
)

const AffiliateDateOptionItem = ({ item }: { item: AffiliateDashboardLeadOptionInterface }) => {
  const { user } = useUser()
  return (
    <li>
      <Tooltip
        wrapperClassName={'w-full'}
        mode={'hover'}
        label={dateTimeFormat(item.date, {
          timeFormat: user?.timeFormat,
          dateFormat: user?.dateFormat,
          timeZone: user?.timezone,
        })}
      >
        <span className={`w-full truncate`}>
          {dateFormat(item.date, {
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        </span>
      </Tooltip>
    </li>
  )
}
export default AffiliateDashboardLeads
